import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchPermissionAdmins, setPermissionOverride } from "../../api/access";
import PermissionScreenLinks from "../../component/access/PermissionScreenLinks";
import ReasonModal from "../../component/access/ReasonModal";
import { useAccess } from "../../context/AccessContext";
import "../../css/AccessManagement.css";
import {
  OVERRIDE_STATES,
  currentOverrideState,
  describeSource,
  formatDateTime,
  getAccessErrorMessage,
  overrideLabel,
} from "../../utils/accessErrors";
import Layout from "../Layout";
import { localInputToIso, useCatalog } from "./accessData";

/**
 * 권한 관리 — 기능별 보기 (spec R-MGMT-03/04, R-FE-06).
 * 권한 하나를 고르면 전체 어드민의 실효 결과·출처·개인 예외를 보여주고, 3상태 토글(역할 따름/허용/차단)로 개인 예외를 바꾼다.
 */
function AccessPermissionView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, adminUserId: myAdminUserId, refreshAccess } = useAccess();
  const canManage = hasPermission("access.permission.manage");
  const { catalog, hiddenKeys, error: catalogError, loading: catalogLoading } = useCatalog();

  const selectedKey = searchParams.get("key") || "";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(null); // { row, next }
  const [expiresAt, setExpiresAt] = useState("");

  const permissionMeta = useMemo(() => {
    for (const group of catalog) {
      const found = group.permissions.find((p) => p.key === selectedKey);
      if (found) return { ...found, domain: group.domain };
    }
    return null;
  }, [catalog, selectedKey]);

  // 숨김 키(쓰는 화면이 없는 키)는 고를 수 없다 — 링크 등으로 들어와도 첫 번째 권한으로 바꾼다
  const selectedHidden = hiddenKeys.has(selectedKey);

  // 첫 진입: 첫 번째 권한 선택
  useEffect(() => {
    if ((!selectedKey || selectedHidden) && catalog.length > 0 && catalog[0].permissions.length > 0) {
      setSearchParams({ key: catalog[0].permissions[0].key }, { replace: true });
    }
  }, [catalog, selectedKey, selectedHidden, setSearchParams]);

  const load = useCallback(async () => {
    // 카탈로그를 받기 전엔 숨김 키인지 몰라 조회를 미룬다(카탈로그 실패 시엔 그대로 조회)
    if (!selectedKey || catalogLoading || selectedHidden) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchPermissionAdmins(selectedKey);
      setRows(data?.admins ?? []);
    } catch (err) {
      setRows([]);
      setError(getAccessErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedKey, catalogLoading, selectedHidden]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.email, String(r.adminUserId)].some((v) => (v || "").toLowerCase().includes(q)),
    );
  }, [rows, query]);

  const openChange = (row, next) => {
    if (!canManage || currentOverrideState(row) === next) return;
    setExpiresAt("");
    setPending({ row, next });
  };

  const expiresIso = localInputToIso(expiresAt);
  const expiresInvalid = Boolean(expiresAt) && (!expiresIso || new Date(expiresIso) <= new Date());

  const confirmChange = async (reason) => {
    const { row, next } = pending;
    await setPermissionOverride(selectedKey, row.adminUserId, {
      effect: next,
      expiresAt: next === "INHERIT" ? undefined : expiresIso,
      reason,
    });
    setPending(null);
    await load();
    if (row.adminUserId === myAdminUserId) await refreshAccess(); // 내 권한이 바뀌었으면 메뉴·버튼 갱신
  };

  return (
    <Layout>
      <div className="access-page">
        <div>
          <h4 className="access-page-title">권한 관리 · 기능별 보기</h4>
          <p className="access-page-desc">
            권한 하나를 고르면 모든 어드민의 실효 결과와 그 이유(출처)를 보여줍니다. 개인 예외는 역할보다 우선하고, 차단이 허용보다
            우선합니다.
          </p>
        </div>

        {!canManage && (
          <div className="access-readonly-note">조회 전용입니다. 변경하려면 '권한 관리 변경' 권한이 필요합니다.</div>
        )}

        <div className="access-section access-toolbar">
          <select
            className="form-select form-select-sm"
            aria-label="권한 선택"
            value={selectedKey}
            onChange={(e) => setSearchParams({ key: e.target.value })}
          >
            {catalog.map((group) => (
              <optgroup key={group.domain} label={group.domain}>
                {group.permissions.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.description} ({p.key})
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          <input
            className="form-control form-control-sm"
            placeholder="이름·이메일·ID 검색"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {permissionMeta?.enforceAlways && (
            <span className="access-badge access-badge-deny" title="shadow 모드에서도 API가 막는 권한">
              항상 시행
            </span>
          )}
        </div>

        {permissionMeta?.screens?.length > 0 && (
          <div className="access-section" data-testid="permission-screens">
            <div className="access-domain-title" style={{ marginTop: 0 }}>
              이 권한을 쓰는 화면
            </div>
            <PermissionScreenLinks key={selectedKey} screens={permissionMeta.screens} />
          </div>
        )}

        {(catalogError || error) && <div className="access-error">{catalogError || error}</div>}

        <div className="access-section access-table-wrap">
          {loading || catalogLoading ? (
            <div className="access-empty">불러오는 중...</div>
          ) : filteredRows.length === 0 ? (
            <div className="access-empty">조회된 어드민이 없습니다</div>
          ) : (
            <table className="table table-hover access-table">
              <thead>
                <tr>
                  <th>어드민</th>
                  <th>실효 결과</th>
                  <th>출처</th>
                  <th>개인 예외</th>
                  <th>설정</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const state = currentOverrideState(row);
                  return (
                    <tr key={row.adminUserId} data-testid={`permission-row-${row.adminUserId}`}>
                      <td>
                        <button
                          type="button"
                          className="access-link"
                          onClick={() => navigate(`/access/admins?adminUserId=${row.adminUserId}`)}
                        >
                          {row.name || "(이름 없음)"}
                        </button>
                        <div className="access-muted">
                          {row.email} · #{row.adminUserId}
                          {row.adminUserId === myAdminUserId && " · 나"}
                        </div>
                      </td>
                      <td>
                        <span className={`access-badge ${row.effective ? "access-badge-allow" : "access-badge-deny"}`}>
                          {row.effective ? "허용" : "차단"}
                        </span>
                      </td>
                      <td data-testid="source">{describeSource(row.source, row.roleKeys)}</td>
                      <td>
                        {row.override ? (
                          <>
                            <span
                              className={`access-badge ${
                                row.override.expired
                                  ? "access-badge-expired"
                                  : row.override.effect === "ALLOW"
                                    ? "access-badge-allow"
                                    : "access-badge-deny"
                              }`}
                            >
                              {overrideLabel(row.override.effect)}
                              {row.override.expired && " · 만료됨"}
                            </span>
                            <div className="access-muted">
                              {row.override.expiresAt ? `만료 ${formatDateTime(row.override.expiresAt)}` : "만료 없음"}
                              {row.override.reason && ` · ${row.override.reason}`}
                            </div>
                          </>
                        ) : (
                          <span className="access-muted">없음</span>
                        )}
                      </td>
                      <td>
                        <div className="access-toggle" role="group" aria-label="개인 예외 설정">
                          {OVERRIDE_STATES.map((s) => (
                            <button
                              type="button"
                              key={s.value}
                              className={state === s.value ? `selected-${s.value}` : ""}
                              aria-pressed={state === s.value}
                              disabled={!canManage}
                              onClick={() => openChange(row, s.value)}
                            >
                              {s.label}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <ReasonModal
        isOpen={Boolean(pending)}
        title="개인 권한 변경"
        danger={pending?.next === "DENY"}
        extraInvalid={pending?.next !== "INHERIT" && expiresInvalid}
        summary={
          pending && (
            <>
              <div>
                <strong>{pending.row.name || `#${pending.row.adminUserId}`}</strong> ({pending.row.email})
                {pending.row.adminUserId === myAdminUserId && " — 내 계정"}
              </div>
              <div>
                {permissionMeta?.description ?? selectedKey} <span className="access-muted">({selectedKey})</span>
              </div>
              <div>
                {overrideLabel(currentOverrideState(pending.row))} → <strong>{overrideLabel(pending.next)}</strong>
              </div>
              {pending.next === "INHERIT" && (
                <div className="access-muted">개인 예외를 지우고 역할에 따른 결과로 돌아갑니다.</div>
              )}
            </>
          )
        }
        onConfirm={confirmChange}
        onClose={() => setPending(null)}
      >
        {pending && pending.next !== "INHERIT" && (
          <>
            <label className="access-label" htmlFor="access-expires-at">
              만료일 (선택 — 비우면 만료 없음)
            </label>
            <input
              id="access-expires-at"
              type="datetime-local"
              className="form-control form-control-sm"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
            {expiresInvalid && <div className="access-error">만료일은 지금 이후여야 합니다.</div>}
          </>
        )}
      </ReasonModal>
    </Layout>
  );
}

export default AccessPermissionView;
